import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, Search } from 'lucide-react';
import { PageHeader } from '@/components/layout/page-header';
import { Badge } from '@/components/ui/badge';
import { BusyOverlay } from '@/components/ui/busy-overlay';
import { Button } from '@/components/ui/button';
import { FilterBar, type FilterField } from '@/components/ui/filter-bar';
import { Input } from '@/components/ui/input';
import { Pagination } from '@/components/ui/pagination';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { localizedName } from '@/components/localized-name';
import { env } from '@/config/env';
import {
  useBookingLocations,
  useSubscriptionPlans,
} from '@/features/manage-subscriptions/subscriptions.api';
import { PAYMENT_STATUS } from '@/features/manage-subscriptions/types';
import { formatCurrency, formatPhone } from '@/lib/format';
import { toApiDate as apiDate } from '@/lib/store-time';
import { cn } from '@/lib/utils';
import {
  useSubscriptionPaymentHistory,
  type SubscriptionPaymentHistoryModel,
  type SubscriptionPaymentRow,
} from './payment-history.api';

/**
 * Subscription payment history — one row per payment taken for a membership.
 *
 * Three changes from the report this replaces:
 *
 * 1. Every row's status was rendered with the *booking* status names, so a
 *    successful payment read "Confirmed" and a failed one read "Cancelled".
 *    The rows carry `UserSubscriptionPaymentStatus`; they say so now.
 * 2. Contact details sat behind a hover popover, which a touch screen has no
 *    way to open — the card had to show them outright, so the two views
 *    disagreed about what a row contained. They are simply shown.
 * 3. Three dates at three stacked lines each is nine lines of a row. The
 *    period the payment bought is one range; the date it was taken is one line.
 */

const ANY = 'ANY';

/**
 * `yyyy-MM-dd` in, wall-clock out — or null, which leaves that end of the range
 * open. Both dates are optional, so the report still defaults to everything.
 */
function toApiDate(value: string, endOfDay: boolean): string | null {
  return value ? apiDate(value, endOfDay) : null;
}

/**
 * These timestamps are wall-clock at the venue, so they are shifted forward by
 * the configured offset and then formatted in UTC.
 */
function venueTime(value: string | null | undefined, withTime: boolean) {
  if (!value) return '—';
  const date = new Date(/[zZ]$/.test(value) ? value : `${value}Z`);
  if (Number.isNaN(date.getTime())) return '—';
  const shifted = new Date(date.getTime() + env.timezoneOffset * 3_600_000);
  return new Intl.DateTimeFormat(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    ...(withTime ? { hour: 'numeric', minute: '2-digit' } : {}),
    timeZone: 'UTC',
  }).format(shifted);
}

function StatusBadge({ row }: { row: SubscriptionPaymentRow }) {
  const { t } = useTranslation();

  if (row.status === PAYMENT_STATUS.successful) {
    return (
      <Badge variant="success">
        {t('subscriptionPaymentHistory.successful')}
      </Badge>
    );
  }
  if (row.status === PAYMENT_STATUS.failed) {
    return (
      <Badge variant="destructive">
        {t('subscriptionPaymentHistory.failed')}
      </Badge>
    );
  }
  return (
    <Badge variant="warning">{t('subscriptionPaymentHistory.pending')}</Badge>
  );
}

function personName(row: SubscriptionPaymentRow) {
  return [row.firstName, row.lastName].filter(Boolean).join(' ') || '—';
}

export function SubscriptionPaymentHistoryPage() {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? 'en';

  const [draft, setDraft] = useState('');
  const [query, setQuery] = useState('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [locationId, setLocationId] = useState(ANY);
  const [subscriptionId, setSubscriptionId] = useState(ANY);
  const [status, setStatus] = useState(ANY);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(20);

  const { data: locations } = useBookingLocations();
  const { data: plans } = useSubscriptionPlans();

  const model: SubscriptionPaymentHistoryModel = useMemo(
    () => ({
      searchTerm: query,
      startDate: toApiDate(start, false),
      endDate: toApiDate(end, true),
      locationId: locationId === ANY ? null : locationId,
      subscriptionId: subscriptionId === ANY ? null : subscriptionId,
      status: status === ANY ? null : Number(status),
      page,
      size,
    }),
    [query, start, end, locationId, subscriptionId, status, page, size]
  );

  const history = useSubscriptionPaymentHistory(model);
  const rows = history.data?.payments ?? [];
  const total = history.data?.total ?? 0;

  const reset =
    <T,>(setter: (value: T) => void) =>
    (value: T) => {
      setter(value);
      setPage(0);
    };

  const filterFields: FilterField[] = [
    {
      id: 'start',
      label: t('subscriptionPaymentHistory.from'),
      value: start,
      onChange: reset(setStart),
      type: 'date',
      resetValue: '',
    },
    {
      id: 'end',
      label: t('subscriptionPaymentHistory.to'),
      value: end,
      onChange: reset(setEnd),
      type: 'date',
      resetValue: '',
    },
    {
      id: 'location',
      label: t('subscriptionPaymentHistory.location'),
      value: locationId,
      onChange: reset(setLocationId),
      options: [
        { value: ANY, label: t('subscriptionPaymentHistory.any') },
        ...(locations ?? []).map((l) => ({
          value: l.id,
          label: localizedName(l, language) || (l.name ?? ''),
        })),
      ],
    },
    {
      id: 'membership',
      label: t('subscriptionPaymentHistory.membership'),
      value: subscriptionId,
      onChange: reset(setSubscriptionId),
      options: [
        { value: ANY, label: t('subscriptionPaymentHistory.any') },
        ...(plans ?? []).map((plan) => ({
          value: plan.id,
          label: localizedName(plan, language),
        })),
      ],
    },
    {
      id: 'status',
      label: t('subscriptionPaymentHistory.status'),
      value: status,
      onChange: reset(setStatus),
      // Pending / successful / failed is an outcome, not an alphabet.
      sort: false,
      options: [
        { value: ANY, label: t('subscriptionPaymentHistory.any') },
        {
          value: String(PAYMENT_STATUS.pending),
          label: t('subscriptionPaymentHistory.pending'),
        },
        {
          value: String(PAYMENT_STATUS.successful),
          label: t('subscriptionPaymentHistory.successful'),
        },
        {
          value: String(PAYMENT_STATUS.failed),
          label: t('subscriptionPaymentHistory.failed'),
        },
      ],
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('subscriptionPaymentHistory.title')}
        description={t('subscriptionPaymentHistory.subtitle')}
      />

      <FilterBar
        busy={history.isFetching}
        search={
          <form
            role="search"
            onSubmit={(event) => {
              event.preventDefault();
              if (draft === query && page === 0) void history.refetch();
              setQuery(draft);
              setPage(0);
            }}
            className="flex gap-2"
          >
            <div className="relative flex-1">
              <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
              <Input
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder={t('subscriptionPaymentHistory.searchPlaceholder')}
                aria-label={t('subscriptionPaymentHistory.searchPlaceholder')}
                className="h-10 pl-9"
              />
            </div>
            <Button type="submit" className="h-10">
              <Search className="sm:hidden" />
              <span className="hidden sm:inline">{t('common.search')}</span>
            </Button>
          </form>
        }
        fields={filterFields}
        summary={
          history.isSuccess ? (
            <p className="text-muted-foreground text-sm">
              {t('subscriptionPaymentHistory.showingOf', {
                shown: rows.length,
                total,
              })}
            </p>
          ) : null
        }
      />

      {history.isError && (
        <div className="border-destructive/30 bg-destructive/8 text-destructive flex items-start gap-2 rounded-xl border p-4 text-sm">
          <AlertCircle className="mt-0.5 size-4 shrink-0" />
          <div className="space-y-2">
            <p>{t('subscriptionPaymentHistory.loadFailed')}</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void history.refetch()}
            >
              {t('common.retry')}
            </Button>
          </div>
        </div>
      )}

      {history.isPending && (
        <div className="divide-y rounded-xl border">
          {Array.from({ length: 6 }).map((_, index) => (
            <div key={index} className="space-y-2 p-3">
              <Skeleton className="h-4 w-48" />
              <Skeleton className="h-3 w-64" />
            </div>
          ))}
        </div>
      )}

      {history.isSuccess && rows.length === 0 && (
        <div className="relative rounded-xl">
          <BusyOverlay show={history.isFetching} />
          <p className="text-muted-foreground rounded-xl border border-dashed py-14 text-center text-sm">
            {t('subscriptionPaymentHistory.noResults')}
          </p>
        </div>
      )}

      {rows.length > 0 && (
        <div className="space-y-3">
          <div className="hidden rounded-xl border lg:block">
            <Table busy={history.isFetching}>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>
                    {t('subscriptionPaymentHistory.name')}
                  </TableHead>
                  <TableHead>
                    {t('subscriptionPaymentHistory.membership')}
                  </TableHead>
                  <TableHead>
                    {t('subscriptionPaymentHistory.period')}
                  </TableHead>
                  <TableHead>
                    {t('subscriptionPaymentHistory.paidOn')}
                  </TableHead>
                  <TableHead className="text-right">
                    {t('subscriptionPaymentHistory.amount')}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>
                      <p className="font-medium">{personName(row)}</p>
                      <p className="text-muted-foreground text-xs">
                        {row.email}
                      </p>
                      {row.phoneNumber && (
                        <p className="text-muted-foreground text-xs tabular-nums">
                          {formatPhone(row.phoneNumber)}
                        </p>
                      )}
                    </TableCell>
                    <TableCell>
                      <p>{row.subscriptionName}</p>
                      <div className="mt-0.5">
                        <StatusBadge row={row} />
                      </div>
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      {venueTime(row.forStartDate, false)} –{' '}
                      {venueTime(row.forEndDate, false)}
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      {venueTime(row.createdAt, true)}
                    </TableCell>
                    <TableCell className="text-right font-semibold tabular-nums">
                      {formatCurrency(row.amount)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <div className="relative lg:hidden">
            <BusyOverlay show={history.isFetching} />
            <div
              className={cn(
                'space-y-2',
                history.isFetching && 'opacity-60 transition-opacity'
              )}
            >
              {rows.map((row) => (
                <div
                  key={row.id}
                  className="bg-card space-y-2 rounded-xl border p-3"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate font-medium">{personName(row)}</p>
                      <p className="text-muted-foreground truncate text-xs">
                        {row.email}
                      </p>
                      {row.phoneNumber && (
                        <p className="text-muted-foreground text-xs tabular-nums">
                          {formatPhone(row.phoneNumber)}
                        </p>
                      )}
                    </div>
                    <p className="shrink-0 font-semibold tabular-nums">
                      {formatCurrency(row.amount)}
                    </p>
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-sm">{row.subscriptionName}</p>
                    <StatusBadge row={row} />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-muted-foreground text-xs">
                        {t('subscriptionPaymentHistory.period')}
                      </p>
                      <p className="text-xs">
                        {venueTime(row.forStartDate, false)} –{' '}
                        {venueTime(row.forEndDate, false)}
                      </p>
                    </div>
                    <div>
                      <p className="text-muted-foreground text-xs">
                        {t('subscriptionPaymentHistory.paidOn')}
                      </p>
                      <p className="text-xs">{venueTime(row.createdAt, true)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <Pagination
            page={page}
            size={size}
            total={total}
            onPageChange={setPage}
            onSizeChange={(next) => {
              setSize(next);
              setPage(0);
            }}
          />
        </div>
      )}
    </div>
  );
}
